import { Stack, Typography } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { useCallback, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import type { AdminApi, StatisticsPeriodKey } from '@/admin/adminApi';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { Button, PageHeader } from '@/ui/kit';

import { clock } from './dashboardModel';
import { PeriodSwitch } from './PeriodSwitch';
import { ReadBlock } from './ReadBlock';
import { StatisticsContent } from './StatisticsContent';
import { parseStatisticsPeriod, STATISTICS_PERIODS } from './statisticsModel';
import { useAdminRead } from './useAdminRead';

/**
 * Statistiques d'usage (P10), version 1 : des compteurs globaux, sans croisement.
 *
 * Il repond a « le produit sert-il, et a qui » sans jamais regarder une famille.
 *
 * - **Un seul appel par chargement** : `GET /admin/statistics?period=`. Chaque
 *   lecture d'une route `/admin/*` ecrit une ligne d'audit (ADR 0035 §4) — la
 *   periode (`?period=` dans l'URL, donc partageable) relit, « Actualiser » et le
 *   retour sur l'onglet relisent, rien d'autre. L'heure de la lecture est ecrite.
 * - **Periodes predefinies seulement** (30 et 90 jours) : ni plage libre, ni
 *   comparaison, ni export, ni decoupage par attribut — **absents, pas grises**
 *   (ADR 0024). Une periode inconnue dans l'URL retombe sur 90 jours, elle n'est
 *   jamais envoyee au serveur.
 * - **Le titre est toujours la**, y compris en chargement, en erreur et en 403 :
 *   un ecran qui change de forme ne perd jamais son titre.
 * - **Un 403 n'est pas une panne** et ne deconnecte pas : le role `support`
 *   suffit.
 */

const TITLE = 'Statistiques d’usage';

/** Retour sur l'onglet : on ne relit qu'apres 30 s, comme les ecrans d'exploitation (une ligne d'audit par lecture). */
export const REFRESH_AFTER_RETURN_MS = 30_000;

export const StatisticsScreen = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parseStatisticsPeriod(searchParams.get('period'));
  const [refreshToken, setRefreshToken] = useState(0);

  const readStatistics = useCallback((api: AdminApi) => api.statistics(period), [period]);
  const statistics = useAdminRead(readStatistics, refreshToken);

  const refresh = useCallback(() => {
    setRefreshToken((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(refresh, REFRESH_AFTER_RETURN_MS);

  const changePeriod = (next: StatisticsPeriodKey): void => {
    setSearchParams({ period: next }, { replace: true });
  };

  return (
    <Stack spacing={3} sx={{ mt: 1, mb: 4 }}>
      <PageHeader
        eyebrow="Pilotage"
        title={TITLE}
        subtitle="Comptes actifs, nouveaux comptes et saisies par nature : des compteurs globaux, jamais le détail d’une famille."
        actions={
          <>
            <PeriodSwitch
              value={period}
              onChange={changePeriod}
              options={STATISTICS_PERIODS}
              label="Période des séries hebdomadaires"
            />
            <Button icon={RefreshCw} disabled={statistics.state.kind === 'loading'} onClick={refresh}>
              Actualiser
            </Button>
          </>
        }
      />

      <ReadBlock state={statistics.state} label={TITLE} onRetry={statistics.reload} skeletonHeight={240}>
        {(data, at) => (
          <>
            <StatisticsContent statistics={data} />
            <Typography component="p" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              {`Relevé à ${clock(at)}. L’écran ne se rafraîchit pas seul : chaque lecture est inscrite au journal d’audit.`}
            </Typography>
          </>
        )}
      </ReadBlock>
    </Stack>
  );
};
